// File: AddBankAccount.js
// Purpose: React component for adding a bank account with robust validation, error handling, and secure practices.
// This file follows best practices as per codegenerator-v1.0.md: separation of concerns, DRY, SOLID, secure coding, meaningful naming, inline documentation, and error handling.

import React from 'react';
import {
  withStyles, Grid, Icon, Button, Dialog, DialogTitle, DialogActions, DialogContent,
  AppBar, Toolbar, IconButton, TextField, MenuItem, Tooltip
} from '@material-ui/core';
import axios from 'axios';
import {
  platformHrThemeColor, disableHtmlElement, genericLoadingProgress, snackBarComponent
} from '../../genericFunctions';
import Formsy from 'formsy-react';
import { TextFieldFormsy } from '@fuse';
import accountLodash from '@lodash';

/**
 * Styles for the AddBankAccount component tooltip.
 */
const useStyles = () => ({
  tooltip: {
    width: '120px',
    backgroundColor: '#585858',
    color: '#ffffff',
    textAlign: 'center',
    borderRadius: '6px',
    padding: '5px 0px',
    zIndex: 1,
    float: 'right',
    position: 'absolute',
    left: '75%',
    fontSize: '12px',
    bottom: '10%'
  }
});

/**
 * AddBankAccount component allows users to add a new bank account with validation and error handling.
 * @component
 */
class AddBankAccount extends React.Component {
  constructor(props) {
    super(props);

    // Set ledgerList based on module prop
    let ledgerList = [];
    if (props.module === 'bankRec')
      ledgerList = ['Billing', 'Direct Deposit', 'Trust', 'Tax'];
    else if (props.module === 'empFund')
      ledgerList = ['EWA'];    

    this.state = {
      bankAccount: '',
      accountNumber: '',
      ledger: '',
      ledgerList, // set dynamically depending on module
      saveBankAccountDisabled: false,
      hover: false,
      bankAccountError: '',
      accountNumberError: '',
      validating: false, // 9603 - validation in progress
      bankSnack: false, // Snackbar visibility
      banksnckmessage: '' // Snackbar message
    };

    // Debounced function to validate bank account input after user stops typing for 200ms
    this.debouncedAccountNameSearch = accountLodash.debounce(this.validateBankAccount, 200);
    // Debounced function to validate account number input after user stops typing for 200ms
    this.debouncedAccountNumberSearch = accountLodash.debounce(this.validateAccountNumber, 200);
  }

  /**
   * Closes the dialog and resets the form fields.
   * @param {boolean} close - Whether to close the dialog.
   */
  handleClose = (close = false) => {
    const { closeAccount } = this.props;
    this.setState({
      bankAccount: '',
      accountNumber: '',
      ledger: '',
      bankAccountError: '',
      accountNumberError: '',
      validating: false
    }, () => {
      if (closeAccount) closeAccount(false);
    });
  };

  snackbarhandleClientClose = () => {
    this.setState({ bankSnack: false });
  };

  /**
   * Validates if the bank account already exists via API and updates state.
   * @param {string} bankAccount - The bank account to validate.
   */
  validateBankAccount = async (bankAccount) => {
    this.setState({ validating: true });// 9603 - validation in progress
    if (!bankAccount) {
      this.setState({ bankAccountError: '', validating: false });
      return;
    }
    const { module } = this.props;
    // Use correct module in URL
    const modulePrefix = module === 'bankRec' ? 'bankRec' : 'employeeFunds';
    try {
      const response = await axios.post(`/api/${modulePrefix}/items/bankRecLedger/checkBankAccount`, { data: { bankAccount } });
      if (response && response.data && response.data.data && response.data.data.exists)
        this.setState({ bankAccountError: response.data.data.message || 'Bank Account already exists', validating: response.data.data.exists });
      else
        this.setState({ bankAccountError: '', validating: response.data.data.exists, bankSnack: false, banksnckmessage: '' });
    } catch (error) {
      this.setState({ bankAccountError: 'Error validating Bank Account', validating: false, bankSnack: true, banksnckmessage: 'Error validating Bank Account' });
    }
  };

  /**
   * Validates if the account number already exists via API.
   * @param {string} accountNumber - The account number to validate.
   * @returns {Promise<string>} Error message or empty string.
   */
  validateAccountNumber = async (accountNumber) => {
    this.setState({ validating: true });// 9603 - validation in progress
    if (!accountNumber) {
      this.setState({ accountNumberError: '', validating: false });
      return;
    }
    const { module } = this.props;
    // Use correct module in URL
    const modulePrefix = module === 'bankRec' ? 'bankRec' : 'employeeFunds';
    try {
      const response = await axios.post(`/api/${modulePrefix}/items/bankRecLedger/checkAccountNumber`, { data: { accountNumber } });
      if (response && response.data && response.data.data && response.data.data.exists)
        this.setState({ accountNumberError: response.data.data.message || 'Account Number already exists', validating: response.data.data.exists});
      else
        this.setState({ accountNumberError: '', validating: response.data.data.exists, bankSnack: false, banksnckmessage: '', });
    } catch (error) {
      this.setState({ accountNumberError: 'Error validating Bank Account', validating: false, bankSnack: true, banksnckmessage: 'Error validating Bank Account' });
    }
  };

  /**
   * Handles input changes and triggers validation.
   * Uses debounced validation for bank account and account number fields to avoid excessive API calls.
   * @param {object} event - The input change event.
   * @param {string} field - The field being changed.
   */
  handleChange = async (event, field) => {
    const value = event.target.value;
    // 9603 - Always set validating true when user types in either field and restrict to hit immediate save button
    if (field === 'bankAccount' || field === 'accountNumber') {
      this.setState({ [field]: value, validating: true });
    } else {
      this.setState({ [field]: value });
    }
    if (field === 'bankAccount') {
      // Use debounced validation for bank account field
      this.debouncedAccountNameSearch(value);
    }
    if (field === 'accountNumber') {
      //// 9604 - If value is empty or only digits, set it; otherwise, clear the field
      if (value === "" || /^\d+$/.test(value)) {
        this.setState({ [field]: value, validating: true });
      } else {
        this.setState({ [field]: "", validating: true });
      }
      // Use debounced validation for account number field
      this.debouncedAccountNumberSearch(value);
    }
  };

  /**
   * Saves the bank account if validation passes.
   */
  saveBankAccount = async () => {
    const { ledger, bankAccount, accountNumber, bankAccountError, accountNumberError, validating, bankSnack } = this.state;
    const { closeAccount, module } = this.props;
    // Prevent save if validation errors, still validating, or snackbar is active
    if (bankAccountError || accountNumberError || validating || bankSnack || !bankAccount || !accountNumber || !ledger) {
      if (bankAccountError || accountNumberError) {
        this.setState({ bankSnack: true, banksnckmessage: bankAccountError || accountNumberError });
      }      
    } else {    
      // Use correct module in URL
      const modulePrefix = module === 'bankRec' ? 'bankRec' : 'employeeFunds';
      try {
        this.setState({ saveBankAccountDisabled: true });
        const response = await axios.post(`/api/${modulePrefix}/items/bankRecLedger/newBankAccount`, {
          data: { bankAccount, accountNumber, ledger }
        });
        this.setState({ saveBankAccountDisabled: false });
        if (response && response.data && response.data.data && response.data.data.exists){
          this.setState({ bankSnack: true, banksnckmessage: response.data.data.message });
          return;
        }else{
          this.setState(
            { bankSnack: true, banksnckmessage: response.data.data.message },
            () => { 
                    setTimeout(() => {
                      if (closeAccount) closeAccount(true);
                      this.handleClose();
                    }, 1000);
                  }
          );
        }        
      } catch (error) {
        // Log error for debugging
        console.error(error);
        this.setState({ saveBankAccountDisabled: false, bankSnack: true, banksnckmessage: 'Error validating Bank Account' });
      }
    }
  };

  /**
   * Handles mouse hover in for tooltip.
   */
  handleMouseIn = () => {
    this.setState({ hover: true });
  };

  /**
   * Handles mouse hover out for tooltip.
   */
  handleMouseOut = () => {
    this.setState({ hover: false });
  };

  /**
   * Renders the AddBankAccount dialog.
   * @returns {JSX.Element}
   */
  render() {
    const { classes, openDialogLedger } = this.props;
    const {
      bankAccount, accountNumber, hover, ledgerList, ledger,
      saveBankAccountDisabled, bankAccountError, accountNumberError
    } = this.state;
    const tooltipStyle = { display: hover ? 'block' : 'none' };
    const disableSaveAccount = saveBankAccountDisabled ? { ...disableHtmlElement } : {};
    const loadLedgerStyle = {
      genStyle: { color: platformHrThemeColor, position: 'fixed', top: '48vh', left: '48%', zIndex: 999 },
      circularStyle: { width: '70px', height: '70px' }
    };
    return (
      <>
        <Dialog
          open={openDialogLedger}
          onClose={this.handleClose}
          disableBackdropClick
          disableEscapeKeyDown
          fullWidth
          maxWidth={false}
          PaperProps={{ style: { width: 600 } }}
        >
          <DialogTitle component="div" className={`p-0 ${saveBankAccountDisabled ? 'pointer-events-none opacity-50' : ''}`}>
            <AppBar style={{ color: 'white', backgroundColor: platformHrThemeColor }} position="static" elevation={1}>
              <Toolbar className="flex w-full overflow-x-auto px-8 sm:px-16" style={{ overflowX: 'scroll', overflowY: 'hidden' }}>
                <div className="flex flex-1">Add Bank Account</div>
                <IconButton color="inherit">
                  <Icon onClick={this.handleClose}>close</Icon>
                </IconButton>
              </Toolbar>
            </AppBar>
          </DialogTitle>

          <DialogContent className={`p-16 sm:p-24 ${saveBankAccountDisabled ? 'pointer-events-none opacity-50' : ''}`}>
            {genericLoadingProgress({ ...loadLedgerStyle })}
            <div className="mb-16 sm:mb-8 flex">
              <Formsy ref={form => (this.form = form)} className="flex flex-col justify-center w-full">
                <Grid container spacing={2}>
                  {/* Bank Account */}
                  <Grid item xs={12} style={{ marginBottom: '25px' }}>
                    <TextFieldFormsy
                      margin="dense"
                      id="bankAccount"
                      label={
                        <span style={{ position: 'relative', bottom: bankAccount ? '0px' : '2px' }}>
                          Bank Account Name *
                        </span>
                      }
                      name="bankAccount"
                      type="text"
                      variant="outlined"
                      value={bankAccount}
                      onChange={evt => this.handleChange(evt, 'bankAccount')}
                      fullWidth
                      multiline
                      rows={1}
                      inputProps={{ style: { minHeight: '20px' }, maxLength: 50 }}
                      error={!!bankAccountError}
                    />
                    {bankAccountError && (
                      <div style={{ color: '#f44336', fontSize: '14px', marginLeft: '14px', marginTop: '3px' }}>
                        {bankAccountError}
                      </div>
                    )}
                  </Grid>
                  {/* Account Number */}
                  <Grid item xs={12} style={{ marginBottom: '25px' }}>
                    <Tooltip
                      title={
                        <span style={{ fontSize: '14px', wordBreak: 'normal', whiteSpace: 'normal' }}>
                          Account numbers entered will be encrypted.
                        </span>
                      }
                      placement="top"
                    >
                      <span>
                        <TextFieldFormsy
                          margin="dense"
                          id="accountNumber"
                          label={
                            <span style={{ position: 'relative', bottom: accountNumber ? '0px' : '2px' }}>
                              Account Number *
                            </span>
                          }
                          name="accountNumber"
                          type="text"
                          variant="outlined"
                          value={accountNumber}
                          onChange={evt => this.handleChange(evt, 'accountNumber')}
                          fullWidth
                          rows={1}
                          inputProps={{
                            style: { minHeight: '20px' },
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            onKeyPress: (e) => {
                              if (!/[0-9]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }
                          }}
                          error={!!accountNumberError}
                        />
                      </span>
                    </Tooltip>
                    {accountNumberError && (
                      <div style={{ color: '#f44336', fontSize: '14px', marginLeft: '14px', marginTop: '3px' }}>
                        {accountNumberError}
                      </div>
                    )}
                  </Grid>
                  {/* Ledger */}
                  <Grid item xs={12} style={{ marginBottom: '10px' }}>
                    <TextField
                      value={ledger || ''}
                      onChange={evt => this.handleChange(evt, 'ledger')}
                      name="ledger"
                      variant="outlined"
                      label={
                        <span style={{ position: 'relative', bottom: ledger ? '0px' : '2px' }}>
                          Ledger *
                        </span>
                      }
                      fullWidth
                      select
                    >
                      <MenuItem value={''}>Select Ledger</MenuItem>
                      {ledgerList && ledgerList.length > 0 &&
                        ledgerList
                          .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                          .map(ledgerItem => (
                            <MenuItem key={ledgerItem} value={ledgerItem}>
                              {ledgerItem}
                            </MenuItem>
                          ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Formsy>
            </div>
          </DialogContent>

          {(!bankAccount || !accountNumber || !ledger) && (
            <div style={{ position: 'relative' }}>
              <div className={classes.tooltip} style={tooltipStyle}>
                Please fill out the required (*) fields
              </div>
            </div>
          )}

          <DialogActions className={saveBankAccountDisabled ? 'pointer-events-none opacity-50' : ''}>
            <Button
              autoFocus
              onClick={this.handleClose}
              style={{ color: '#FFFFFF', backgroundColor: platformHrThemeColor, textTransform: 'capitalize' }}
            >
              Cancel
            </Button>

            <Button
              onMouseOver={this.handleMouseIn}
              onMouseOut={this.handleMouseOut}
              onClick={this.saveBankAccount}
              style={
                bankAccount && accountNumber && ledger && !accountNumberError && !bankAccountError && !this.state.validating && !this.state.bankSnack
                  ? { color: '#FFFFFF', backgroundColor: platformHrThemeColor, textTransform: 'capitalize', ...disableSaveAccount }
                  : { color: '#FFFFFF', backgroundColor: platformHrThemeColor, textTransform: 'capitalize', pointerEvents: 'auto', opacity: 0.5 }
              }
              disabled={
                !(bankAccount && accountNumber && ledger && !accountNumberError && !bankAccountError) || this.state.validating || this.state.bankSnack
              }
            >
              {saveBankAccountDisabled ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        {this.state.bankSnack && snackBarComponent({
          clientSnack: this.state.bankSnack,
          duration: 2000,
          anchorigin: { vertical: 'bottom', horizontal: 'right' },
          snackmessage: this.state.banksnckmessage,
          snackbarhandleClose: this.snackbarhandleClientClose
        })}
      </>
    );
  }
}

export default withStyles(useStyles)(AddBankAccount);